import { useEffect, useState, useCallback } from 'react';
import { openPayload } from '../../lib/oem-vault';
import { resolveOemConflict } from '../../lib/oem-offline';
import { recoverOnlineCommand } from '../../lib/oem-pending';
import { offlineDatabase, type PendingMutation, type SyncConflict } from '../../lib/offline-db';
import type { OemContext } from '../../lib/auth';
import type { OemWorkspace } from '../../lib/oem-client';
export function OfflineQueue({
  context,
  userId,
  workspace,
  onSynchronized,
}: {
  readonly context: OemContext;
  readonly userId: string;
  readonly workspace: OemWorkspace;
  readonly onSynchronized: () => Promise<void>;
}) {
  const [pending, setPending] = useState<PendingMutation[]>([]),
    [conflicts, setConflicts] = useState<SyncConflict[]>([]),
    [quarantined, setQuarantined] = useState(0),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  const readQueue = useCallback(async () => {
    const rows = await offlineDatabase.mutations
      .where('[organizationId+userId]')
      .equals([context.organizationId, userId])
      .toArray();
    const readable = [];
    for (const row of rows) {
      try {
        const payload = await openPayload<{ projectId: string }>(
          userId,
          context.organizationId,
          row.payload,
        );
        if (payload.projectId === context.projectId) readable.push({ ...row, payload });
      } catch {
        /* sealed or expired partition stays inaccessible and preserved */
      }
    }

    const conflicts = [];
    for (const row of await offlineDatabase.conflicts
      .where('[organizationId+userId]')
      .equals([context.organizationId, userId])
      .toArray()) {
      try {
        const localPayload = await openPayload<{ projectId: string }>(
          userId,
          context.organizationId,
          row.localPayload,
        );
        const serverPayload = await openPayload(userId, context.organizationId, row.serverPayload);
        if (localPayload.projectId === context.projectId)
          conflicts.push({ ...row, localPayload, serverPayload });
      } catch {
        /* sealed or expired partition stays inaccessible and preserved */
      }
    }

    const quarantined = await offlineDatabase.quarantine
      .where('[organizationId+userId]')
      .equals([context.organizationId, userId])
      .count();
    return { pending: readable, conflicts, quarantined };
  }, [context.organizationId, context.projectId, userId]);
  async function load() {
    const rows = await readQueue();
    setPending(rows.pending);
    setConflicts(rows.conflicts);
    setQuarantined(rows.quarantined);
  }
  useEffect(() => {
    void readQueue()
      .then((rows) => {
        setPending(rows.pending);
        setConflicts(rows.conflicts);
        setQuarantined(rows.quarantined);
      })
      .catch(() => setError('draft_inventory_unavailable'));
    const update = () =>
      void readQueue()
        .then((rows) => {
          setPending(rows.pending);
          setConflicts(rows.conflicts);
          setQuarantined(rows.quarantined);
        })
        .catch(() => setError('draft_inventory_unavailable'));
    addEventListener('sqi-offline-queue-changed', update);
    return () => removeEventListener('sqi-offline-queue-changed', update);
  }, [readQueue]);
  async function sync() {
    setBusy(true);
    try {
      await onSynchronized();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'sync_unavailable');
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="card">
      <div className="eyebrow">LOCAL DRAFTS</div>
      <h2>ร่างในอุปกรณ์และข้อขัดแย้ง</h2>
      <p>
        {pending.length} รอซิงก์ · {conflicts.length} ขัดแย้ง · {quarantined} ร่างเดิมรอ mapping
      </p>
      <p>ร่างเดิมยังคงอยู่ใน quarantine ไม่มีการอนุมัติ รับชำระ หรือยืนยันคำสั่งซื้อแบบออฟไลน์</p>
      <button
        className="action secondary"
        disabled={busy || !navigator.onLine}
        onClick={() => void sync()}
      >
        ตรวจสิทธิ์และซิงก์ร่าง
      </button>
      {pending.map((m) => (
        <p key={m.id}>
          {m.entityType} · base v{m.baseVersion} · {m.lastError ?? 'รอส่ง'} · mutation{' '}
          {m.idempotencyKey}
          {m.entityType.startsWith('oem:online:') ? (
            <button
              className="action secondary"
              disabled={busy || !navigator.onLine}
              onClick={() => {
                setBusy(true);
                void offlineDatabase.mutations
                  .get(m.id)
                  .then((row) => {
                    if (!row) throw Error('pending_not_found');
                    return recoverOnlineCommand(context, userId, row);
                  })
                  .then(() => load())
                  .catch((e: unknown) =>
                    setError(e instanceof Error ? e.message : 'recovery_unavailable'),
                  )
                  .finally(() => setBusy(false));
              }}
            >
              ตรวจ receipt ก่อนส่งซ้ำ
            </button>
          ) : null}
        </p>
      ))}
      {conflicts.map((c) => {
        const payload = c.localPayload as {
          body?: { payload?: { content?: Record<string, unknown> }; target?: { id: string } };
        };
        const local = payload.body?.payload?.content ?? {};
        const server =
          (c.serverPayload as { record?: OemWorkspace['records'][number] })?.record ??
          workspace.records.find((r) => r.id === payload.body?.target?.id);
        return (
          <article className="scope" key={c.id}>
            <h3>ต้องทบทวนความต่างก่อนสร้างเวอร์ชันใหม่</h3>
            <p>
              ร่าง base v{c.baseVersion} · server v{c.serverVersion}
            </p>
            <table className="table">
              <thead>
                <tr>
                  <th>ข้อมูล</th>
                  <th>ร่างในอุปกรณ์</th>
                  <th>เซิร์ฟเวอร์</th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(local).map((key) => (
                  <tr key={key}>
                    <td>{key}</td>
                    <td>{JSON.stringify(local[key])}</td>
                    <td>{JSON.stringify(server?.content[key] ?? null)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p>
              เปิดตัวแก้ไขเวอร์ชันปัจจุบันเพื่อทบทวนและสร้าง successor ที่ได้รับสิทธิ์
              ไม่มีการรวมอัตโนมัติ
            </p>
            <button
              className="action secondary"
              disabled={busy}
              onClick={() =>
                void resolveOemConflict(context, userId, c.id, 'keep_server').then(load)
              }
            >
              เก็บฉบับเซิร์ฟเวอร์
            </button>
            <button
              className="action secondary"
              disabled={busy || !navigator.onLine}
              onClick={() =>
                void resolveOemConflict(context, userId, c.id, 'branch')
                  .then(load)
                  .catch((e: unknown) =>
                    setError(e instanceof Error ? e.message : 'branch_unavailable'),
                  )
              }
            >
              เก็บร่างเป็น branch ใหม่ · คงประวัติเดิม
            </button>
            <details>
              <summary>รวมข้อมูลด้วยตนเองกับเวอร์ชันปัจจุบัน</summary>
              <textarea
                aria-label="ข้อมูลรวมที่ทบทวนแล้ว JSON"
                defaultValue={JSON.stringify({ ...server?.content, ...local }, null, 2)}
                id={`merge-${c.id}`}
              />
              <button
                className="action secondary"
                disabled={
                  busy ||
                  !navigator.onLine ||
                  (c.serverPayload as { submitted?: boolean }).submitted
                }
                onClick={() => {
                  try {
                    const merged = JSON.parse(
                      (document.getElementById(`merge-${c.id}`) as HTMLTextAreaElement).value,
                    ) as Record<string, unknown>;
                    void resolveOemConflict(context, userId, c.id, 'manual_merge', merged)
                      .then(load)
                      .catch((e: unknown) =>
                        setError(e instanceof Error ? e.message : 'merge_unavailable'),
                      );
                  } catch {
                    setError('invalid_merge_json');
                  }
                }}
              >
                บันทึกร่างรวมบน target และ hash ปัจจุบัน
              </button>
            </details>
          </article>
        );
      })}
      {error ? (
        <p role="alert" className="note-danger">
          {error}
        </p>
      ) : null}
    </section>
  );
}
